import { membershipNameType } from './constant'

/**
 * Dragon Boat Festival (端午节) promotion — Pro plan only.
 *
 * Offer: 50% off the *monthly* Pro plan for the first 3 months (a Stripe coupon with
 * duration=repeating / duration_in_months=3 carries the "3 months" automatically — the
 * frontend only communicates it). Pure date-gated like the UNESCO/CNY promos: no feature
 * flag, no commented code. When the window closes every surface reverts to its normal view.
 *
 * Audience (who is offered the upgrade):
 *   - Free learners (this includes the weekly_test_cohort free learners)
 *   - Weekly subscribers
 * Standard, Pro, and Teacher learners are intentionally excluded.
 *
 * Prices are NEVER hardcoded here — the regular Pro monthly price comes live from Stripe and
 * the promo price is derived as 50% of it (see `getDragonBoatPromoPricing`). Only the campaign
 * framing (coupon code, dates, percentage, duration) lives in this module.
 */
export const DRAGON_BOAT_PROMO = {
  couponCode: 'DRAGONBOAT2026',
  billing: 'monthly' as const,
  // Anchored to Hong Kong time (UTC+8), matching the rest of the app's story cadence.
  startDate: new Date('2026-06-18T00:00:00+08:00'), // Jun 18, 12:00am HKT
  endDate: new Date('2026-06-22T23:59:59+08:00'), // Jun 22, 11:59pm HKT
  discountPercent: 50,
  durationMonths: 3,
} as const

export type PromoTimeLeft = {
  days: number
  hours: number
  minutes: number
  seconds: number
}

const emptyTimeLeft = (): PromoTimeLeft => ({ days: 0, hours: 0, minutes: 0, seconds: 0 })

export function isDragonBoatPromoActive(now: Date = new Date()): boolean {
  return now >= DRAGON_BOAT_PROMO.startDate && now <= DRAGON_BOAT_PROMO.endDate
}

export function getDragonBoatPromoEndDate(): Date {
  return DRAGON_BOAT_PROMO.endDate
}

export function getDragonBoatPromoTimeLeft(now: Date = new Date()): PromoTimeLeft {
  const diff = DRAGON_BOAT_PROMO.endDate.getTime() - now.getTime()
  if (diff <= 0) return emptyTimeLeft()
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  }
}

/**
 * Derive the displayed pricing from the live Stripe monthly Pro price. Returns null when the
 * price is unavailable so callers can render a graceful loading/fallback state instead of a
 * made-up number.
 */
export function getDragonBoatPromoPricing(
  regularMonthlyPrice?: number | null,
): { regularMonthly: number; promoMonthly: number } | null {
  if (!regularMonthlyPrice || isNaN(regularMonthlyPrice) || regularMonthlyPrice <= 0) return null
  const promoMonthly = (regularMonthlyPrice * (100 - DRAGON_BOAT_PROMO.discountPercent)) / 100
  return { regularMonthly: regularMonthlyPrice, promoMonthly }
}

export type DragonBoatAudienceContext = {
  membershipName?: string
  now?: Date
}

/**
 * Pure audience test (ignores the date window): is this learner someone the Dragon Boat upgrade
 * is aimed at? Free (incl. weekly_test_cohort) or Weekly. Standard subscribers are excluded.
 */
export function isDragonBoatAudience({ membershipName }: DragonBoatAudienceContext): boolean {
  if (!membershipName) return false
  if (membershipName === membershipNameType.Free) return true
  if (membershipName === membershipNameType.Weekly) return true
  // Standard, Pro, Teacher — not the audience.
  return false
}

/** Active window AND in-audience: the gate for showing the offer on any surface. */
export function isUserEligibleForDragonBoatPromo(ctx: DragonBoatAudienceContext): boolean {
  const now = ctx.now ?? new Date()
  return isDragonBoatPromoActive(now) && isDragonBoatAudience({ ...ctx, now })
}

/**
 * Whether the /setting#plan Pro card should render the promo treatment (dragon-boat video,
 * 50%-off monthly pricing, countdown). Only for the monthly Pro card, for an eligible learner,
 * when no manual coupon is applied and it isn't already their current plan.
 */
export function shouldApplyDragonBoatPromoToPro({
  planValue,
  billing,
  isCurrentPlan,
  hasManualCoupon,
  membershipName,
  now = new Date(),
}: {
  planValue?: string
  billing: 'monthly' | 'yearly'
  isCurrentPlan: boolean
  hasManualCoupon: boolean
  membershipName?: string
  now?: Date
}): boolean {
  if (!isDragonBoatPromoActive(now)) return false
  if (hasManualCoupon) return false
  if (isCurrentPlan) return false
  if (planValue !== membershipNameType.Pro) return false
  if (billing !== DRAGON_BOAT_PROMO.billing) return false
  return isDragonBoatAudience({ membershipName })
}
